import { Group, Paper, SimpleGrid, Skeleton, Stack, Text } from '@mantine/core'

import type { Health } from '@/api/whatsapiStats'
import { useWhatsApiPeriod } from '@/context/WhatsApiPeriodContext'
import { useHealth, useOverview, isNoApiKeys } from '@/hooks/useWhatsApiStats'
import type { EscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

import { HealthStatusBadge, type HealthStatus } from './StatusBadge'

const ERROR_RATE_THRESHOLD = 0.05

function mapHealthStatus(health: Health | undefined): HealthStatus | null {
  if (!health) return null
  if (health.database === 'disconnected' || health.gateway === 'disconnected') {
    return 'down'
  }
  if (health.status === 'degraded') return 'degraded'
  return 'ok'
}

interface NumberCardProps {
  title: string
  value: string
  subtitle?: string
  color?: string
  loading?: boolean
}

function NumberCard({ title, value, subtitle, color, loading = false }: NumberCardProps) {
  return (
    <Paper shadow="xs" p="md" withBorder>
      <Text size="xs" c="dimmed" fw={500}>
        {title}
      </Text>
      {loading ? (
        <Skeleton height={32} width="60%" mt={8} />
      ) : (
        <Text size="xl" fw={700} mt={4} c={color}>
          {value}
        </Text>
      )}
      {subtitle && (
        <Text size="xs" c="dimmed" mt={2}>
          {subtitle}
        </Text>
      )}
    </Paper>
  )
}

function formatNumber(n: number | undefined | null): string {
  if (n === undefined || n === null) return '0'
  return n.toLocaleString('pt-BR')
}

function formatLatency(ms: number | undefined | null): string {
  if (ms === undefined || ms === null) return '0 ms'
  return `${Math.round(ms).toLocaleString('pt-BR')} ms`
}

function formatErrorRate(rate: number | undefined | null): string {
  if (rate === undefined || rate === null) return '0%'
  return `${(rate * 100).toFixed(2)}%`
}

interface StatCardsRowProps {
  escopo?: EscopoWhatsApi
}

export function StatCardsRow({ escopo }: StatCardsRowProps = {}) {
  const { period } = useWhatsApiPeriod()
  const overviewQuery = useOverview(period, { escopo })
  // A organização não tem rota de saúde: o hook não dispara e o card não aparece.
  const healthQuery = useHealth({ escopo })
  const comSaude = escopo?.tipo !== 'organizacao'

  const overviewRaw = overviewQuery.data
  const overview = overviewRaw && !isNoApiKeys(overviewRaw) ? overviewRaw : undefined
  const healthRaw = healthQuery.data
  const health = healthRaw && !isNoApiKeys(healthRaw) ? healthRaw : undefined
  const overviewLoading = overviewQuery.isLoading
  const healthLoading = healthQuery.isLoading

  const sent = overview?.sent ?? 0
  const failed = overview?.failed ?? 0
  const errorRate = overview?.error_rate ?? 0
  const p95 = overview?.p95_latency_ms ?? 0
  const isEmpty = !overviewLoading && sent === 0 && failed === 0 && errorRate === 0 && p95 === 0

  const errorRateColor = errorRate > ERROR_RATE_THRESHOLD ? 'red' : undefined

  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 1, sm: 2, md: comSaude ? 4 : 3 }} spacing="md">
        {comSaude && (
          <Paper shadow="xs" p="md" withBorder>
            <Text size="xs" c="dimmed" fw={500}>
              Status
            </Text>
            <Group mt={8}>
              <HealthStatusBadge
                status={mapHealthStatus(health)}
                loading={healthLoading}
                size="md"
              />
            </Group>
          </Paper>
        )}

        <NumberCard
          title="Enviadas"
          value={formatNumber(sent)}
          loading={overviewLoading}
        />

        <NumberCard
          title="Taxa de erro"
          value={formatErrorRate(errorRate)}
          subtitle={`${formatNumber(failed)} falhadas`}
          color={errorRateColor}
          loading={overviewLoading}
        />

        <NumberCard
          title="p95 latência"
          value={formatLatency(p95)}
          loading={overviewLoading}
        />
      </SimpleGrid>

      {isEmpty && (
        <Text size="sm" c="dimmed" data-testid="stat-cards-empty">
          Nenhum dado disponível para este período
        </Text>
      )}
    </Stack>
  )
}
